"use client";

// These files are the single source of truth for BOTH distribution channels
// of the Magnus chat UI (npm package + shadcn registry). Edit here; never
// fork. The chiclet architecture mirrors `@magnus/react`'s
// `chiclets/chiclet-renderer.tsx`: same `<ref>` contract, same registry
// shape, so a chiclet component written for one runs in the other.
import { c as _c } from "react/compiler-runtime";
import { BookOpen, BookOpenText, CornerDownRightIcon, Database, Globe, MessageSquare, UserRound } from "lucide-react";
import { type ComponentType, createContext, type MouseEvent, type ReactNode, use, useMemo } from "react";

/** Props every inline `<ref>` chiclet receives from the markdown renderer. */
export interface ChicletProps {
  children?: ReactNode;
  label: string;
  refid: string;
  type: string;
}
export const CHICLET_LINK_STYLE = "inline-flex items-center gap-1 align-middle underline decoration-foreground/30 underline-offset-2 hover:decoration-foreground/60 transition-colors text-foreground cursor-pointer";
function getTrailingChildren(label: string, children: ReactNode): ReactNode {
  return label && children != null && String(children).trim() !== "" ? children : null;
}

/** Maps an inline `<ref>` chiclet `type` to the component that renders it. */
export type ChicletRegistry = Record<string, ComponentType<ChicletProps>>;

// ── Chat actions (lets chiclets act on the surrounding chat) ──────────────

export interface ChicletChatActions {
  canSend: boolean;
  send: (text: string) => void;
}
const ChicletChatActionsContext = createContext<ChicletChatActions | null>(null);

/** Provided by `<MagnusChat>` so action chiclets (prompt-link) can send. */
export const ChicletChatActionsProvider = ChicletChatActionsContext.Provider;

// ── Built-in chiclets ──────────────────────────────────────────────────────

function WebSearchChiclet(t0) {
  const $ = _c(10);
  const {
    refid,
    label,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Globe aria-hidden={true} className="h-3.5 w-3.5 shrink-0" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== label || $[2] !== refid) {
    t2 = <a className={CHICLET_LINK_STYLE} href={refid} rel="noopener noreferrer" target="_blank">{t1}{label}</a>;
    $[1] = label;
    $[2] = refid;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== label) {
    t3 = getTrailingChildren(label, children);
    $[4] = children;
    $[5] = label;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

/** Cap decoded prompt length to prevent arbitrarily large payloads. */
const MAX_PROMPT_LENGTH = 2000;

// Mirrors `promptLinkText` in @workspace/utils/prompt-link. Registry blocks
// ship to consumers outside the monorepo, so they cannot import it.
function decodePrompt(value: string): string {
  // Form-encoded spaces arrive as +. Normalize before decoding so %2B stays +.
  const normalized = value.replace(/\+/g, " ");
  try {
    return decodeURIComponent(normalized);
  } catch {
    return normalized;
  }
}
const PROMPT_LINK_STYLE = "flex w-fit items-center gap-1 my-1 font-medium underline decoration-foreground/30 underline-offset-2 hover:decoration-foreground/60 transition-colors text-foreground cursor-pointer";
function PromptLinkChiclet(t0) {
  const $ = _c(20);
  const {
    refid,
    label,
    children
  } = t0;
  const chat = use(ChicletChatActionsContext);
  let t1;
  if ($[0] !== refid) {
    const raw = decodePrompt(refid);
    t1 = raw.length > MAX_PROMPT_LENGTH ? raw.slice(0, MAX_PROMPT_LENGTH) : raw;
    $[0] = refid;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const promptText = t1;
  const canSend = chat?.canSend ?? false;
  let t2;
  if ($[2] !== canSend || $[3] !== chat || $[4] !== promptText) {
    t2 = e => {
      e.preventDefault();
      if (chat && canSend) {
        chat.send(promptText);
      }
    };
    $[2] = canSend;
    $[3] = chat;
    $[4] = promptText;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const onClick = t2;
  const t3 = !canSend;
  const t4 = `Send follow-up: ${label}`;
  const t5 = `${PROMPT_LINK_STYLE}${canSend ? "" : " opacity-40 pointer-events-none cursor-not-allowed"}`;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <CornerDownRightIcon className="size-3.5 shrink-0" />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== label || $[8] !== onClick || $[9] !== promptText || $[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
    t7 = <a aria-disabled={t3} aria-label={t4} className={t5} href="#" onClick={onClick} role="button" title={promptText}>{t6}{label}</a>;
    $[7] = label;
    $[8] = onClick;
    $[9] = promptText;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== children || $[15] !== label) {
    t8 = getTrailingChildren(label, children);
    $[14] = children;
    $[15] = label;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}

/**
 * Non-interactive fallback chip for reference types whose full experience
 * needs host integration the standalone package doesn't ship (in-app chat
 * navigation, the Confluence and knowledge base page viewers, Entra profile
 * popovers). Renders
 * the same inline style with a matching icon; hosts that have those
 * integrations override the type via the `chiclets` registry prop.
 */
function staticChiclet(Icon: ComponentType<{
  className?: string;
}>) {
  return function StaticChiclet({
    label,
    children
  }: ChicletProps) {
    return <>
        <span className="inline-flex items-center gap-1 align-middle text-foreground">
          <Icon aria-hidden className="h-3.5 w-3.5 shrink-0" />
          {label}
        </span>
        {getTrailingChildren(label, children)}
      </>;
  };
}

/**
 * The chiclets this block renders out of the box — same type keys as the
 * Magnus web app. Extend or override per surface via the `chiclets` prop on
 * `<MagnusChat>`.
 */
export const DEFAULT_CHICLETS: ChicletRegistry = {
  "web-search": WebSearchChiclet,
  "prompt-link": PromptLinkChiclet,
  chat: staticChiclet(MessageSquare),
  confluence: staticChiclet(BookOpenText),
  "knowledge-page": staticChiclet(BookOpen),
  databricks: staticChiclet(Database),
  "entra-id-user": staticChiclet(UserRound)
};

// ── Registry context + renderer ───────────────────────────────────────────

const ChicletRegistryContext = createContext<ChicletRegistry>(DEFAULT_CHICLETS);
export interface ChicletRegistryProviderProps {
  /** Surface-specific chiclets, merged over (and overriding) the defaults. */
  chiclets?: ChicletRegistry;
  children: ReactNode;
}
export function ChicletRegistryProvider(t0) {
  const $ = _c(5);
  const {
    chiclets,
    children
  } = t0;
  let t1;
  if ($[0] !== chiclets) {
    t1 = chiclets ? {
      ...DEFAULT_CHICLETS,
      ...chiclets
    } : DEFAULT_CHICLETS;
    $[0] = chiclets;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const value = t1;
  let t2;
  if ($[2] !== children || $[3] !== value) {
    t2 = <ChicletRegistryContext.Provider value={value}>{children}</ChicletRegistryContext.Provider>;
    $[2] = children;
    $[3] = value;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function useChicletRegistry(): ChicletRegistry {
  return use(ChicletRegistryContext);
}

/**
 * Streamdown `<ref>` component: resolves the chiclet for `type` from the
 * active registry, falling back to the plain label when none is registered.
 */
export function ChicletRenderer(props) {
  const $ = _c(8);
  const registry = useChicletRegistry();
  const type = String(props.type ?? "");
  const refid = String(props.refid ?? "");
  const label = String(props.label ?? "");
  const Component = registry[type];
  if (!Component) {
    let t0;
    if ($[0] !== label) {
      t0 = <span>{label}</span>;
      $[0] = label;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] !== Component || $[3] !== label || $[4] !== props.children || $[5] !== refid || $[6] !== type) {
    t0 = <Component label={label} refid={refid} type={type}>{props.children}</Component>;
    $[2] = Component;
    $[3] = label;
    $[4] = props.children;
    $[5] = refid;
    $[6] = type;
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  return t0;
}